import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Layers, Target, Plug, AudioLines, ShieldCheck, LogOut, Languages, Menu, X, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", key: "nav_dashboard", icon: LayoutDashboard, end: true },
  { to: "/use-cases", key: "nav_usecases", icon: Layers },
  { to: "/absorption", key: "nav_absorption", icon: Target },
  { to: "/integrations", key: "nav_integrations", icon: Plug },
  { to: "/audio-logs", key: "nav_audio", icon: AudioLines },
  { to: "/admin", key: "nav_admin", icon: ShieldCheck, adminOnly: true },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const { t, toggleLang, lang, isRTL } = useLang();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const items = NAV.filter((n) => !n.adminOnly || isAdmin);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shrink-0">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold leading-tight truncate">{t("app_name")}</p>
          <p className="text-[11px] text-muted-foreground truncate">{t("app_sub")}</p>
        </div>
      </div>
      <nav className="flex-1 space-y-1 p-3 overflow-y-auto no-scrollbar">
        {items.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) => cn(
                "flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors",
                isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              )}>
              <Icon className="h-[18px] w-[18px] shrink-0" />
              <span className="truncate">{t(n.key)}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t p-3">
        <div className="flex items-center gap-3 rounded-xl px-3 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-foreground text-sm font-bold shrink-0">
            {(user?.name || "U").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate">{user?.name}</p>
            <p className="text-[11px] text-muted-foreground capitalize truncate">{(user?.role || "").replace("_", " ")}</p>
          </div>
          <button data-testid="logout-btn" onClick={doLogout} className="text-muted-foreground hover:text-destructive p-1.5 rounded-lg hover:bg-secondary">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className={cn("fixed inset-y-0 z-30 hidden lg:flex w-64 flex-col bg-card border-e", isRTL ? "right-0" : "left-0")}>
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className={cn("absolute inset-y-0 w-72 bg-card shadow-xl", isRTL ? "right-0" : "left-0")}>
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className={cn("lg:ps-64")}>
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b bg-card/90 backdrop-blur-md px-4 sm:px-6">
          <button className="lg:hidden p-2 -ms-2" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="flex-1" />
          <button data-testid="lang-toggle-button" onClick={toggleLang}
            className="inline-flex items-center gap-2 rounded-full border bg-background px-3.5 py-2 text-sm font-semibold hover:bg-secondary transition-colors">
            <Languages className="h-4 w-4" />
            <span>{lang === "en" ? "العربية" : "English"}</span>
          </button>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-30 lg:hidden bg-card/95 backdrop-blur-md border-t flex items-center justify-around px-1 py-1.5">
        {items.slice(0, 5).map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`bottomnav-${n.key}`}
              className={({ isActive }) => cn("flex flex-col items-center gap-0.5 rounded-lg px-2 py-1.5 text-[10px] font-medium min-w-0",
                isActive ? "text-primary" : "text-muted-foreground")}>
              <Icon className="h-5 w-5" />
              <span className="truncate max-w-[60px]">{t(n.key).split(" ")[0]}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
